// Common
import {
  type AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, pairwise } from 'rxjs/operators';

// Components
import { FiltersPanelComponent } from '@modules/knowledge/components/filters-panel/filters-panel.component';

// Service
import { type StitchFiltersHistoryRecord } from '@modules/common/services/history.service';
import { KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { KpResultCountsService } from '@modules/knowledge/services/kp-result-counts.service';
import { StateService } from '@modules/settings/services/state.service';

// Types
import { STICKY_INCLUDES_PENDING_FOLLOWUP } from '@modules/common/components/list/base-stitch-list.component';
import { Tab } from '@modules/common/types/tab';
import { KPListType } from '@modules/knowledge/types/kp-list-type';
import { ADVANCED_SEARCH_NAMESPACE } from '@modules/search/injection-tokens/advanced-search-namespace.injection-token';
import { HOST_ADVANCED_SEARCH_SERVICE } from '@modules/search/injection-tokens/host-advanced-search-service.injection-token';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';

const NO_MARKERS: StitchFiltersHistoryRecord = { knots: [], tags: [], connections: [] };

/** The related tabs' saved sort & filter: each tab restores its own when it mounts. */
const RELATED_FILTER_KEYS = [StateKey.kpKnotsState, StateKey.kpTagsState, StateKey.kpConnectionsState] as const;

@Component({
  selector: 'app-knowledge-panel',
  templateUrl: './knowledge-panel.component.html',
  styleUrls: ['./knowledge-panel.component.less'],
  standalone: false,
  providers: [
    // The KP owns an isolated advanced-search instance so a chip's "Search" on a KP card never reaches
    // the host app's search. The KP reads nothing from it and clears what it stored under `as.kp`.
    AdvancedSearchService,
    { provide: ADVANCED_SEARCH_NAMESPACE, useValue: 'kp' },
    // The type menu's counts, built from the same KP-scoped search as the sections.
    KpResultCountsService,
    // The enclosing (non-KP) instance — inside the dynamic panel, the DP-scoped one — captured
    // before the override above shadows it. KP sections hand it to full-form modals so their search
    // affordances target the surrounding context's search, never the KP's isolated result list.
    {
      provide: HOST_ADVANCED_SEARCH_SERVICE,
      useFactory: () => inject(AdvancedSearchService, { skipSelf: true, optional: true }),
    },
    // Highlight pending follow-ups in place; inherited by result lists via the element injector.
    { provide: STICKY_INCLUDES_PENDING_FOLLOWUP, useValue: true },
  ],
})
export class KnowledgePanelComponent implements AfterViewInit {
  protected readonly KPListType = KPListType;
  public resultsTypeStateKey = StateKey.kpResultsStitchType;
  public currentTab: 'knots' | 'tags' | 'connections' = 'knots';
  public tabsValues: Tab<'knots' | 'tags' | 'connections'>[] = [
    { title: 'Knots', value: 'knots' },
    { title: 'Tags', value: 'tags' },
    { title: 'Connections', value: 'connections' },
  ];

  protected readonly kpListType = signal(KPListType.message);

  /**
   * The Selected row's height. The knowledge half grows by it, so selecting never shrinks the Related box: the
   * room comes out of the results, and the half's saved height stays the Related box's.
   */
  protected readonly selectedRowHeight = signal(0);

  @ViewChild(FiltersPanelComponent, { read: ElementRef, static: true })
  private readonly selectedRow: ElementRef<HTMLElement>;

  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly kpService = inject(KnowledgePanelService);
  private readonly stateService = inject(StateService);

  /**
   * Closing the item in panel 3 clears the saved sort & filter of all three related tabs, not only the one on
   * show: the others would restore theirs when next opened (Johanna, 2026-10-21; #2554 review MD-01). A cleared
   * state restores as the tab's default.
   */
  private readonly resetRelatedFiltersOnClose = this.kpService
    .getContextStitchItem()
    .pipe(
      pairwise(),
      filter(([before, now]) => !!before && !now),
      takeUntilDestroyed(),
    )
    .subscribe(() => RELATED_FILTER_KEYS.forEach((key) => this.stateService.setState(key, null)));

  /** Nothing is open in panel 3: the panel shows no Related band (stitch-app-shell skin). */
  protected readonly nothingOpen = toSignal(this.kpService.getContextStitchItem().pipe(map((item) => !item)), {
    initialValue: true,
  });

  // Everything this panel's queries must honour — in the Dynamic Panel that includes the selection
  // mirrored from the 3rd panel. It lives on the service so all four lists in this panel cannot drift
  // into answering different questions.
  //
  // Nothing else feeds Results. With no selection anywhere it issues no request: it shows its idle
  // instruction while an item is open and asks for one while nothing is. The open item drives the
  // neighbour LISTS (through contextItems), never the result list.
  private readonly queryFilter = toSignal(this.kpService.getQueryFilter(), { initialValue: NO_MARKERS });

  protected readonly resultsKnots = computed(() => this.queryFilter().knots);
  protected readonly resultsTags = computed(() => this.queryFilter().tags);
  protected readonly resultsConnections = computed(() => this.queryFilter().connections);

  constructor() {
    inject(AdvancedSearchService).resetState();
    this.kpService.resetKeywords();
  }

  public ngAfterViewInit(): void {
    if (!this.selectedRow || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.height === this.selectedRowHeight()) return;
      this.selectedRowHeight.set(entry.contentRect.height);
      // Resize the half before this frame paints, or the Related box would flash squeezed.
      this.changeDetector.detectChanges();
    });
    observer.observe(this.selectedRow.nativeElement);
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  /**
   * Actions
   */

  handleClickItem(_item: StitchItemSummaryDto) {
    // TODO: implement
  }

  handleTabsChange(value: 'knots' | 'tags' | 'connections') {
    this.currentTab = value;
    this.changeDetector.detectChanges();
  }
}
